// The real transport: the callables in europe-west1 and the account's
// Firestore subtree. Firebase is imported on first use only: the client
// module throws at import when the config is blank, and the data layer
// must load, and keep answers, without it.

import { ASSESSMENT_IDS } from "../schema/types";
import type {
  AssessmentRun,
  FinishRunRequest,
  Profile,
  ProfileResponse,
  RunResponse,
  SaveAnswersRequest,
  SaveProfileRequest,
  StartRedoRequest,
} from "../schema/types";
import type { Transport } from "./types";

/** The region the functions repo deploys the callables to. */
export const FUNCTIONS_REGION = "europe-west1";

async function firebase() {
  const [client, functions, firestore] = await Promise.all([
    import("../lib/firebase"),
    import("firebase/functions"),
    import("firebase/firestore"),
  ]);
  return { client, functions, firestore };
}

async function call<Request, Response = RunResponse>(name: string, request: Request): Promise<Response> {
  const { client, functions } = await firebase();
  const callable = functions.httpsCallable<Request, Response>(functions.getFunctions(client.app, FUNCTIONS_REGION), name);
  return (await callable(request)).data;
}

export function createFirebaseTransport(): Transport {
  return {
    saveAnswers: (request: SaveAnswersRequest) => call("saveAnswers", request),
    finishRun: (request: FinishRunRequest) => call("finishRun", request),
    startRedo: (request: StartRedoRequest) => call("startRedo", request),
    saveProfile: (request: SaveProfileRequest) => call<SaveProfileRequest, ProfileResponse>("saveProfile", request),

    async fetchRuns(accountId) {
      const { client, firestore } = await firebase();
      const runs = firestore.collection(client.db, "accounts", accountId, "runs");
      // One document per assessment; the limit is the bound, not a page.
      const snapshot = await firestore.getDocs(firestore.query(runs, firestore.limit(ASSESSMENT_IDS.length)));
      return snapshot.docs.map((document) => document.data() as AssessmentRun);
    },

    async fetchProfile(accountId) {
      const { client, firestore } = await firebase();
      const snapshot = await firestore.getDoc(firestore.doc(client.db, "accounts", accountId, "profile", "current"));
      return snapshot.exists() ? (snapshot.data() as Profile) : null;
    },
  };
}
